import { toast } from "bulma-toast";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import Conversation from "../../components/ednaConversations/Conversation";
import Header from "../../components/header/Header";
import { Loader } from "../../components/loader/Loader";
import locale from "../../constants/locale";
import useMobileResponsive from "../../hooks/useMobileResponsive";
import { EdnaChatType } from "../../models/EdnaChat";
import { useStore } from "../../stores";
import styles from "./Teammates.module.scss";
import Chat from "./components/Chat";
import { Collapse } from "../../components";
import Mentions from "./components/mentions/Mentions";
import { LuAtSign } from "react-icons/lu";
import routes from "../../constants/routes";
import { FaCaretRight } from "react-icons/fa";
import { FaCaretDown } from "react-icons/fa";
import Threads from "./components/threads/Threads";
import { PiChatCircleDots } from "react-icons/pi";
import { startTour } from "../../utils/tour";
import useTeammates, { ChatInterface } from "../../hooks/useTeammates";
import HubModal from "./components/hubs/HubModal";
import { GoPlus } from "react-icons/go";

const TeammatesWrapper = () => {
  const { t } = useTranslation();
  const isMobile = useMobileResponsive();
  const { selectedWorkspace, workspaceLoaded } = useStore("workspaces");
  const { newMessageAlerts } = useStore("ednaChats");
  const { loadHubs } = useStore("hubs");
  const [loading, setLoading] = useState(true);
  const [showHubModal, setShowHubModal] = useState(false);

  const history = useHistory();
  const { loadUsers } = useStore("users");

  const { userId } = useParams<{
    userId?: string;
  }>();

  useEffect(() => {
    if (!workspaceLoaded || !selectedWorkspace?.id) {
      return;
    }
    const load = async () => {
      setLoading(true);
      await loadUsers(selectedWorkspace.id);
      await loadHubs();
      startTour("dms");
      setLoading(false);
    };
    load();
  }, [workspaceLoaded, selectedWorkspace, loadUsers, loadHubs]);

  const { chats } = useTeammates();

  return (
    <div className="w-100-responsive">
      {!isMobile && <Header isMobile={isMobile} title={t(locale.teammates)} />}

      <div className={clsx(styles.h90, "is-flex is-flex-grow-1")}>
        <div
          className={clsx(
            "all-dms full-screen-mobile is-overflow-y-auto py-3 px-4 is-flex-shrink-0 has-border-right",
            styles.container,
            {
              "is-active": !userId,
            }
          )}
        >
          <div
            onClick={() =>
              history.push(
                routes.teammates
                  .replace(":workspaceId", String(selectedWorkspace?.id))
                  .replace(":userId", "mentions")
              )
            }
            className={clsx(
              "has-text-black has-text-weight-semibold is-relative is-flex w-100 px-3 py-2 is-list-item is-clickable is-align-items-center",
              {
                "is-active": userId === "mentions",
              }
            )}
          >
            <LuAtSign />
            <span className="ml-1">{t(locale.mentions)}</span>
          </div>
          <div
            onClick={() =>
              history.push(
                routes.teammates
                  .replace(":workspaceId", String(selectedWorkspace?.id))
                  .replace(":userId", "threads")
              )
            }
            className={clsx(
              "has-text-black has-text-weight-semibold is-relative is-flex w-100 px-3 py-2 is-list-item is-clickable is-align-items-center",
              {
                "is-active": userId === "threads",
              }
            )}
          >
            <PiChatCircleDots />
            <span className="ml-1">{t(locale.threads)}</span>
          </div>
          <CollapseWrapper
            name={locale.hubs}
            key={
              "hub-" + chats.filter((f) => f.type === EdnaChatType.HUB).length
            }
            newMessages={chats
              .filter((f) => f.type === EdnaChatType.HUB)
              ?.some((f) => newMessageAlerts?.includes(f.id || ""))}
          >
            {chats
              .filter((f) => f.type === EdnaChatType.HUB)
              ?.map((chat) => (
                <Chat
                  chat={chat}
                  newMessage={newMessageAlerts?.indexOf(chat.id || "") !== -1}
                />
              ))}
            <div
              className="px-5 has-text-black is-relative is-flex w-100 py-2 is-list-item is-clickable is-align-items-center"
              onClick={() => setShowHubModal(true)}
              style={{
                fontSize: 13,
              }}
            >
              <GoPlus size={14} />
              <span className="ml-1">Create Hub</span>
            </div>
          </CollapseWrapper>
          <CollapseWrapper
            name={locale.aITeammates}
            newMessages={chats
              .filter((f) =>
                [EdnaChatType.BOT, EdnaChatType.AI_TEAM].includes(f.type)
              )
              ?.some((f) => newMessageAlerts?.includes(f.id || ""))}
          >
            {chats
              .filter((f) =>
                [EdnaChatType.BOT, EdnaChatType.AI_TEAM].includes(f.type)
              )
              ?.map((chat) => (
                <Chat
                  chat={chat}
                  newMessage={newMessageAlerts?.indexOf(chat.id || "") !== -1}
                />
              ))}
          </CollapseWrapper>

          <CollapseWrapper
            name={locale.humanTeammates}
            newMessages={chats
              .filter((f) => f.type === EdnaChatType.SINGLE)
              ?.some((f) => newMessageAlerts?.includes(f.id || ""))}
          >
            {chats
              .filter((f) => f.type === EdnaChatType.SINGLE)
              ?.map((chat) => (
                <Chat
                  chat={chat}
                  newMessage={newMessageAlerts?.indexOf(chat.id || "") !== -1}
                />
              ))}
          </CollapseWrapper>

          <CollapseWrapper
            name={locale.teams}
            key={chats.filter((f) => f.type === EdnaChatType.CHANNEL).length}
            newMessages={chats
              .filter((f) => f.type === EdnaChatType.CHANNEL)
              ?.some((f) => newMessageAlerts?.includes(f.id || ""))}
          >
            {chats
              .filter((f) => f.type === EdnaChatType.CHANNEL)
              ?.map((chat) => (
                <Chat
                  chat={chat}
                  newMessage={newMessageAlerts?.indexOf(chat.id || "") !== -1}
                />
              ))}
          </CollapseWrapper>
        </div>
        <div className="is-relative w-100 is-flex">
          {!loading && userId && !["mentions", "threads"].includes(userId) && (
            <Teamates chats={chats} />
          )}
          {!loading && userId === "mentions" && <Mentions chats={chats} />}
          {!loading && userId === "threads" && <Threads chats={chats} />}
        </div>
      </div>

      {showHubModal && <HubModal open onClose={() => setShowHubModal(false)} />}
    </div>
  );
};

const Teamates = observer(({ chats }: { chats: ChatInterface[] }) => {
  const { selectedWorkspace } = useStore("workspaces");
  const {
    findingChat,
    firstOrCreateChat,
    setWorkspaceId,
    chatInfo,
    setChatInfo,
  } = useStore("ednaChats");
  const [loadingChat, setLoadingChat] = useState(false);
  const auth = useStore("auth");

  const { userId } = useParams<{
    userId?: string;
  }>();

  const getChat = useCallback(async () => {
    if (!userId) return;

    setLoadingChat(true);
    const chat = chats?.find((f) => f.id === userId);

    if (!chat) {
      toast({
        message: "Chat not found",
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
      return;
    }

    try {
      setChatInfo({
        id: chat.id,
        type: chat.type,
        name: chat.name || "",
        profileUrl: chat.image,
        extras: chat.extras,
      });

      let room;

      if (chat.type === EdnaChatType.AI_TEAM) {
        room = `ai-team-${chat.id}`;
      } else if (chat.type === EdnaChatType.CHANNEL) {
        room = `livecard-${chat.id}`;
      } else if (chat.type === EdnaChatType.HUB) {
        room = `hub-${chat.id}`;
      } else {
        room = [chat.id, auth.user?.id || ""]
          .sort((a, b) => a.localeCompare(b))
          .join("|");
      }
      setWorkspaceId(chat.extras?.workspaceId ?? selectedWorkspace?.id);
      await firstOrCreateChat(chat.type, room);
      setLoadingChat(false);
    } catch (error) {
      console.log("error", error);
    }
  }, [
    userId,
    chats,
    setChatInfo,
    setWorkspaceId,
    selectedWorkspace?.id,
    firstOrCreateChat,
    auth.user?.id,
  ]);

  useEffect(() => {
    getChat();
  }, [getChat]);

  return (
    <>
      {loadingChat || findingChat ? (
        <div className={styles.loader}>
          <Loader />
        </div>
      ) : (
        <Conversation chatInfo={chatInfo} />
      )}
    </>
  );
});

export const CollapseWrapper = ({
  children,
  name,
  newMessages,
}: {
  children: any;
  name: string;
  newMessages?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Collapse
      key={name}
      defaultExpanded={true}
      renderTrigger={(trigger) => (
        <div
          {...trigger}
          className={clsx(
            "has-text-weight-semibold is-relative is-flex w-100 px-3 py-2 is-list-item has-text-black is-clickable is-align-items-center"
          )}
        >
          {trigger.isExpanded ? <FaCaretDown /> : <FaCaretRight />}
          <span className="ml-1">{t(name)}</span>
          {newMessages && (
            <span
              className="badge is-danger no-box-shadow mr-4"
              style={{
                top: "1.2rem",
              }}
            />
          )}
        </div>
      )}
    >
      {children}
    </Collapse>
  );
};

export default observer(TeammatesWrapper);
